import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';
import StatusBadge from '@/components/base/StatusBadge';
import CaseCard from '@/components/base/CaseCard';
import useSeo from '@/hooks/useSeo';
import { getCaseById, getRelatedCases } from '@/lib/caseTypes';
import { rankedList } from '@/mocks/cases';
import CaseTimeline from '@/pages/case/detail/components/CaseTimeline';
import StatementList from '@/pages/case/detail/components/StatementList';

const SITE = 'https://eogtxkn.cn';

const CaseDetail = () => {
  const { id } = useParams();
  const item = id ? getCaseById(id) : undefined;
  const related = item ? getRelatedCases(item) : [];

  const jsonLd = useMemo(() => {
    if (!item) return undefined;
    return [
      {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: item.title,
        description: item.summary,
        image: item.cover,
        datePublished: item.timeline[0]?.date,
        dateModified: item.updatedAt,
        inLanguage: 'zh-CN',
        articleSection: item.category,
        keywords: item.tags.join(', '),
        author: { '@type': 'Organization', name: '瓜田追踪' },
        publisher: { '@type': 'Organization', name: '瓜田追踪' },
        mainEntityOfPage: `${SITE}/case/${item.id}`,
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: '反转档案', item: `${SITE}/#archive` },
          { '@type': 'ListItem', position: 3, name: item.category },
          { '@type': 'ListItem', position: 4, name: item.title },
        ],
      },
    ];
  }, [item]);

  useSeo({
    title: item ? `${item.title}｜后续追踪 · 瓜田追踪` : '事件未找到 · 瓜田追踪',
    description: item
      ? `${item.summary} 当前状态：${item.status}，已记录 ${item.reversal} 次反转。查看完整时间线与当事人回应。`
      : '该追踪档案不存在或已被合并，请返回首页查看其他正在追踪的事件。',
    keywords: item ? `${item.category},${item.tags.join(',')},爆料后续,事件反转` : '爆料后续,事件反转',
    canonical: item ? `${SITE}/case/${item.id}` : `${SITE}/`,
    ogType: 'article',
    jsonLd,
  });

  if (!item) {
    return (
      <SiteLayout>
        <div className="layout-shell flex min-h-[60vh] flex-col items-center justify-center py-20 text-center">
          <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-3xl text-foreground-500">
            <i className="ri-file-unknow-line" aria-hidden="true" />
          </span>
          <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">没有找到这条追踪档案</h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            它可能已经被合并到其他事件，或者链接输入有误。你可以返回首页，从热搜追踪榜里继续浏览。
          </p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            <i className="ri-arrow-left-line" aria-hidden="true" />
            返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const hotList = rankedList.slice(0, 4).filter((c) => c.id !== item.id).slice(0, 3);

  return (
    <SiteLayout>
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '反转档案', to: '/#archive' },
          { label: item.category },
          { label: item.title },
        ]}
      />

      <article>
        <header className="border-b border-background-200 bg-background-100">
          <div className="layout-shell py-8 md:py-10">
            <div className="flex flex-wrap items-center gap-3">
              <StatusBadge status={item.status} size="md" />
              <span className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800">{item.category}</span>
            </div>
            <h1 className="mt-4 max-w-4xl font-heading text-2xl font-black leading-snug tracking-tight text-foreground-950 md:text-[38px] md:leading-[1.2]">
              {item.title}
            </h1>
            <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-foreground-600 md:text-base">{item.summary}</p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-3-line" aria-hidden="true" />
                涉及 {item.handle}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-refresh-line text-primary-600" aria-hidden="true" />
                反转 {item.reversal} 次
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line" aria-hidden="true" />
                {item.readTime}阅读
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line" aria-hidden="true" />
                最近更新 {item.updatedAt}
              </span>
            </div>
          </div>
        </header>

        <div className="layout-shell py-8 md:py-10">
          <div className="overflow-hidden rounded-lg border border-background-300/70">
            <img
              src={item.cover}
              alt={`${item.title} - 事件追踪配图`}
              title={`${item.title} · 瓜田追踪`}
              className="h-56 w-full object-cover object-top md:h-80"
            />
          </div>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-8">
              <section>
                <h2 className="font-heading text-xl font-bold text-foreground-950">事件概要</h2>
                <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-foreground-700">
                  <p>{item.summary}</p>
                  <p>
                    以下时间线按照事件发生的先后顺序整理，每个节点都标注了类型：爆料、反转、回应、辟谣与官方信息。
                    已被后续信息修正的节点会保留在原位，方便你对照事件的变化过程。
                  </p>
                </div>
                <div className="mt-5 flex flex-wrap gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-background-300 bg-background-100 px-3 py-1 text-xs text-foreground-700">
                      #{tag}
                    </span>
                  ))}
                </div>
              </section>

              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950">事件时间线</h2>
                <CaseTimeline timeline={item.timeline} />
              </section>

              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950">当事人与各方回应</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  以下内容整理自公开渠道的表述，仅代表相关方立场。涉及争议的部分，请以正式声明与官方通报为准。
                </p>
                <StatementList statements={item.statements} />
              </section>

              {related.length > 0 && (
                <section className="mt-10">
                  <h2 className="font-heading text-xl font-bold text-foreground-950">相关追踪</h2>
                  <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {related.map((rel) => (
                      <CaseCard key={rel.id} item={rel} />
                    ))}
                  </div>
                </section>
              )}
            </div>

            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-24">
                <div className="rounded-lg border border-background-300/70 bg-background-100 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">追踪档案</h2>
                  <dl className="mt-4 space-y-3 text-sm">
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">涉及账号</dt>
                      <dd className="text-right font-medium text-foreground-900">{item.handle}</dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">事件分类</dt>
                      <dd className="text-right font-medium text-foreground-900">{item.category}</dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">当前状态</dt>
                      <dd className="text-right"><StatusBadge status={item.status} /></dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">反转次数</dt>
                      <dd className="text-right font-medium text-foreground-900">{item.reversal} 次</dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">追踪人数</dt>
                      <dd className="text-right font-medium text-foreground-900">{item.follow}k</dd>
                    </div>
                    <div className="flex items-start justify-between gap-3">
                      <dt className="text-foreground-500">最近更新</dt>
                      <dd className="text-right font-medium text-foreground-900">{item.updatedAt}</dd>
                    </div>
                  </dl>
                </div>

                <div className="mt-5 rounded-lg border border-accent-200 bg-accent-50 p-5">
                  <div className="flex items-center gap-2 text-accent-700">
                    <i className="ri-edit-2-line" aria-hidden="true" />
                    <h2 className="font-heading text-sm font-bold">回应或纠错</h2>
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                    如果你是本事件相关方，欢迎提供可查证的说明材料。核实后我们会更新时间线并记录更正。
                  </p>
                  <Link
                    to="/#about"
                    className="mt-3 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-medium text-primary-700 transition-colors hover:text-primary-800"
                  >
                    前往联系与订阅
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>

                <div className="mt-5 rounded-lg border border-background-300/70 bg-background-100 p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950">热门追踪</h2>
                  <ul className="mt-4 divide-y divide-background-200">
                    {hotList.map((hot) => (
                      <li key={hot.id}>
                        <Link to={`/case/${hot.id}`} className="group flex gap-3 py-3">
                          <div className="h-14 w-16 flex-none overflow-hidden rounded-md border border-background-200">
                            <img
                              src={hot.cover}
                              alt={`${hot.title} - 追踪配图`}
                              title={`${hot.title} · 瓜田追踪`}
                              className="h-full w-full object-cover object-top"
                            />
                          </div>
                          <div className="min-w-0">
                            <h3 className="line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
                              {hot.title}
                            </h3>
                            <span className="mt-1 block text-xs text-foreground-500">{hot.updatedAt} 更新</span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                  <Link to="/#rank" className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors hover:text-primary-800">
                    查看完整追踪榜
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </Link>
                </div>

                <p className="mt-5 text-xs leading-relaxed text-foreground-500">
                  内容声明：本页信息整理自公开渠道，仅用于呈现事件进展，不构成对任何个人或机构的事实认定。最终事实请以官方通报与生效法律文书为准。
                </p>
              </div>
            </aside>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
};

export default CaseDetail;